import React, { useState } from 'react';
import { FaLeaf, FaWater, FaBug, FaSeedling, FaCloudSun, FaTree, FaChartLine, FaDownload, FaInfoCircle, FaArrowUp, FaArrowDown, FaArrowRight, FaHistory, FaGlobeAmericas, FaBullseye, FaDatabase, FaChevronDown, FaChevronUp, FaTimes } from 'react-icons/fa';
import '../styles/sustainability.css';

const SustainabilityIndex = () => {
  const [dimensions, setDimensions] = useState([
    { 
      id: 1, 
      name: 'Soil Health', 
      score: 85, 
      icon: <FaSeedling />, 
      trend: 'improving',
      description: 'Measures the quality, structure, and nutrient content of soil.',
      calculation: [
        { name: 'Organic matter content', percentage: 25 },
        { name: 'Soil structure and tilth', percentage: 20 },
        { name: 'Biological activity', percentage: 20 },
        { name: 'Nutrient balance', percentage: 20 },
        { name: 'Absence of contaminants', percentage: 15 }
      ],
      strategies: [
        'Cover cropping',
        'Reduced tillage',
        'Organic matter additions',
        'Crop rotation'
      ],
      chartData: [
        { month: 'Jan', value: 78 },
        { month: 'Feb', value: 80 },
        { month: 'Mar', value: 82 },
        { month: 'Apr', value: 83 },
        { month: 'May', value: 85 }
      ]
    },
    { 
      id: 2, 
      name: 'Water Management', 
      score: 72, 
      icon: <FaWater />, 
      trend: 'stable',
      description: 'Evaluates water use efficiency, conservation practices, and water quality protection.',
      calculation: [
        { name: 'Irrigation efficiency', percentage: 30 },
        { name: 'Runoff prevention', percentage: 25 },
        { name: 'Water quality protection', percentage: 25 },
        { name: 'Water conservation practices', percentage: 20 }
      ],
      strategies: [
        'Drip irrigation',
        'Rainwater harvesting',
        'Buffer zones near water bodies',
        'Precision irrigation scheduling'
      ],
      chartData: [
        { month: 'Jan', value: 71 },
        { month: 'Feb', value: 72 },
        { month: 'Mar', value: 73 },
        { month: 'Apr', value: 72 },
        { month: 'May', value: 72 }
      ]
    },
    { 
      id: 3, 
      name: 'Biodiversity', 
      score: 68, 
      icon: <FaTree />, 
      trend: 'improving',
      description: 'Assesses the variety of plant and animal species present in and around agricultural lands.',
      calculation: [
        { name: 'Crop diversity', percentage: 25 },
        { name: 'Beneficial insect populations', percentage: 20 },
        { name: 'Wildlife habitat', percentage: 20 },
        { name: 'Native plant preservation', percentage: 20 },
        { name: 'Pollinator support', percentage: 15 }
      ],
      strategies: [
        'Hedgerows and buffer strips',
        'Diverse crop rotations',
        'Habitat corridors',
        'Reduced pesticide use'
      ],
      chartData: [
        { month: 'Jan', value: 62 },
        { month: 'Feb', value: 64 },
        { month: 'Mar', value: 65 },
        { month: 'Apr', value: 67 },
        { month: 'May', value: 68 }
      ]
    },
    { 
      id: 4, 
      name: 'Pest Management', 
      score: 76, 
      icon: <FaBug />, 
      trend: 'stable',
      description: 'Evaluates approaches to managing pests while minimizing environmental impact.',
      calculation: [
        { name: 'Integrated Pest Management adoption', percentage: 35 },
        { name: 'Reduced chemical dependency', percentage: 25 },
        { name: 'Biological control utilization', percentage: 20 },
        { name: 'Pest monitoring practices', percentage: 20 }
      ],
      strategies: [
        'Beneficial insect habitat',
        'Crop rotation',
        'Resistant varieties',
        'Trap crops'
      ],
      chartData: [
        { month: 'Jan', value: 75 },
        { month: 'Feb', value: 76 },
        { month: 'Mar', value: 77 },
        { month: 'Apr', value: 76 },
        { month: 'May', value: 76 }
      ]
    },
    { 
      id: 5, 
      name: 'Climate Resilience', 
      score: 62, 
      icon: <FaCloudSun />, 
      trend: 'declining',
      description: 'Measures the farm\'s ability to withstand and adapt to climate change impacts.',
      calculation: [
        { name: 'Drought resistance', percentage: 25 },
        { name: 'Flood mitigation', percentage: 25 },
        { name: 'Temperature extremes adaptation', percentage: 25 },
        { name: 'Weather variability planning', percentage: 25 }
      ],
      strategies: [
        'Diversified cropping systems',
        'Water storage systems',
        'Shade structures',
        'Windbreaks'
      ],
      chartData: [
        { month: 'Jan', value: 67 },
        { month: 'Feb', value: 65 },
        { month: 'Mar', value: 64 },
        { month: 'Apr', value: 63 },
        { month: 'May', value: 62 }
      ]
    },
    { 
      id: 6, 
      name: 'Energy Efficiency', 
      score: 79, 
      icon: <FaLeaf />, 
      trend: 'improving',
      description: 'Evaluates energy use and renewable energy adoption in farming operations.',
      calculation: [
        { name: 'Fuel efficiency', percentage: 30 },
        { name: 'Electricity conservation', percentage: 25 },
        { name: 'Renewable energy use', percentage: 25 },
        { name: 'Equipment efficiency', percentage: 20 }
      ],
      strategies: [
        'Solar power adoption',
        'Efficient equipment',
        'Reduced tillage',
        'Optimized transportation'
      ],
      chartData: [
        { month: 'Jan', value: 75 },
        { month: 'Feb', value: 76 },
        { month: 'Mar', value: 77 },
        { month: 'Apr', value: 78 },
        { month: 'May', value: 79 }
      ]
    },
    { 
      id: 7, 
      name: 'Crop Rotation', 
      score: 88, 
      icon: <FaLeaf />, 
      trend: 'improving',
      description: 'Assesses the implementation of diverse crop rotation practices.',
      calculation: [
        { name: 'Rotation diversity', percentage: 30 },
        { name: 'Rotation length', percentage: 25 },
        { name: 'Cover crop integration', percentage: 25 },
        { name: 'Nutrient management through rotation', percentage: 20 }
      ],
      strategies: [
        'Multi-year rotations',
        'Including legumes in rotation',
        'Alternating deep and shallow-rooted crops',
        'Balancing nutrient-depleting and nutrient-building crops'
      ],
      chartData: [
        { month: 'Jan', value: 84 },
        { month: 'Feb', value: 85 },
        { month: 'Mar', value: 86 },
        { month: 'Apr', value: 87 },
        { month: 'May', value: 88 }
      ]
    },
    { 
      id: 8, 
      name: 'Waste Reduction', 
      score: 81, 
      icon: <FaLeaf />, 
      trend: 'stable',
      description: 'Measures efforts to minimize waste and implement circular economy principles.',
      calculation: [
        { name: 'Composting practices', percentage: 30 },
        { name: 'Packaging reduction', percentage: 25 },
        { name: 'Material reuse', percentage: 25 },
        { name: 'Food waste prevention', percentage: 20 }
      ],
      strategies: [
        'On-farm composting',
        'Biodegradable materials',
        'Precision harvesting',
        'Waste-to-energy systems'
      ],
      chartData: [
        { month: 'Jan', value: 80 },
        { month: 'Feb', value: 81 },
        { month: 'Mar', value: 81 },
        { month: 'Apr', value: 80 },
        { month: 'May', value: 81 }
      ]
    },
    { 
      id: 9, 
      name: 'Carbon Sequestration', 
      score: 65, 
      icon: <FaLeaf />, 
      trend: 'improving',
      description: 'Evaluates practices that capture and store carbon in soil and biomass.',
      calculation: [
        { name: 'Soil organic carbon levels', percentage: 35 },
        { name: 'Perennial plantings', percentage: 25 },
        { name: 'Agroforestry practices', percentage: 20 },
        { name: 'Reduced soil disturbance', percentage: 20 }
      ],
      strategies: [
        'Cover cropping',
        'Agroforestry',
        'Biochar application',
        'Permanent pasture'
      ],
      chartData: [
        { month: 'Jan', value: 61 },
        { month: 'Feb', value: 62 },
        { month: 'Mar', value: 63 },
        { month: 'Apr', value: 64 },
        { month: 'May', value: 65 }
      ]
    },
    { 
      id: 10, 
      name: 'Community Engagement', 
      score: 90, 
      icon: <FaLeaf />, 
      trend: 'stable',
      description: 'Assesses involvement with and support of the local community.',
      calculation: [
        { name: 'Local employment', percentage: 25 },
        { name: 'Community education', percentage: 25 },
        { name: 'Transparency practices', percentage: 25 },
        { name: 'Community support initiatives', percentage: 25 }
      ],
      strategies: [
        'Farm tours and workshops',
        'Local hiring practices',
        'Community-supported agriculture',
        'School partnerships'
      ],
      chartData: [
        { month: 'Jan', value: 89 },
        { month: 'Feb', value: 90 },
        { month: 'Mar', value: 90 },
        { month: 'Apr', value: 91 },
        { month: 'May', value: 90 }
      ]
    },
    { 
      id: 11, 
      name: 'Economic Viability', 
      score: 77, 
      icon: <FaChartLine />, 
      trend: 'improving',
      description: 'Measures the financial sustainability and resilience of farming operations.',
      calculation: [
        { name: 'Profitability', percentage: 30 },
        { name: 'Market diversity', percentage: 25 },
        { name: 'Risk management', percentage: 25 },
        { name: 'Value-added activities', percentage: 20 }
      ],
      strategies: [
        'Diverse income streams',
        'Direct marketing',
        'Value-added products',
        'Cooperative arrangements'
      ],
      chartData: [
        { month: 'Jan', value: 73 },
        { month: 'Feb', value: 74 },
        { month: 'Mar', value: 75 },
        { month: 'Apr', value: 76 },
        { month: 'May', value: 77 }
      ]
    },
    { 
      id: 12, 
      name: 'Resource Sharing', 
      score: 83, 
      icon: <FaLeaf />, 
      trend: 'improving',
      description: 'Evaluates collaborative resource use and sharing among farms and communities.',
      calculation: [
        { name: 'Equipment sharing', percentage: 30 },
        { name: 'Knowledge exchange', percentage: 25 },
        { name: 'Labor cooperation', percentage: 25 },
        { name: 'Infrastructure sharing', percentage: 20 }
      ],
      strategies: [
        'Equipment cooperatives',
        'Farmer networks',
        'Shared processing facilities',
        'Collaborative marketing'
      ],
      chartData: [
        { month: 'Jan', value: 79 },
        { month: 'Feb', value: 80 },
        { month: 'Mar', value: 81 },
        { month: 'Apr', value: 82 },
        { month: 'May', value: 83 }
      ]
    }
  ]);

  const [expandedDimension, setExpandedDimension] = useState(null);
  const [overallScore, setOverallScore] = useState(77);
  const [complianceStatus, setComplianceStatus] = useState('Compliant');
  const [lastUpdated, setLastUpdated] = useState('May 12, 2023');
  const [region, setRegion] = useState('DMV Region');

  const getScoreColor = (score) => {
    if (score >= 80) return 'var(--accent-green)';
    if (score >= 60) return 'var(--accent-yellow)';
    return 'var(--accent-red)';
  };

  const getScoreCategory = (score) => {
    if (score >= 80) return 'high';
    if (score >= 60) return 'medium';
    return 'low';
  };

  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'improving': return <FaArrowUp />;
      case 'declining': return <FaArrowDown />;
      case 'stable': return <FaArrowRight />;
      default: return null;
    }
  };

  const getTrendColor = (trend) => {
    switch(trend) {
      case 'improving': return 'var(--accent-green)';
      case 'declining': return 'var(--accent-red)';
      case 'stable': return 'var(--accent-yellow)';
      default: return 'var(--text-color)';
    }
  };

  const handleGenerateReport = () => {
    alert(`Generating sustainability report for ${region}`);
  };

  const handleExpandDimension = (id) => {
    if (expandedDimension === id) {
      setExpandedDimension(null);
    } else {
      setExpandedDimension(id);
      setTimeout(() => {
        const element = document.querySelector(`.dimension-card[data-id="${id}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const renderExpandedContent = (dimension) => {
    return (
      <div className="dimension-expanded-content">
        <button 
          className="close-expanded-button"
          onClick={(e) => {
            e.stopPropagation();
            setExpandedDimension(null);
          }}
        >
          <FaTimes />
        </button>
        
        <div className="dimension-section">
          <h5>Description</h5>
          <p>{dimension.description}</p>
        </div>
        
        <div className="dimension-section">
          <h5>Calculation Factors</h5>
          <div className="calculation-factors">
            {dimension.calculation.map((factor, index) => (
              <div key={index} className="calculation-factor">
                <div className="factor-name">{factor.name}</div>
                <div className="factor-percentage">{factor.percentage}%</div>
                <div className="factor-bar">
                  <div 
                    className="factor-bar-fill" 
                    style={{ width: `${factor.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="dimension-section">
          <h5>Improvement Strategies</h5>
          <ul className="strategies-list">
            {dimension.strategies.map((strategy, index) => (
              <li key={index}>{strategy}</li>
            ))}
          </ul>
        </div>
        
        <div className="dimension-section">
          <h5>Trend (Last 5 Months)</h5>
          <div className="trend-chart">
            {dimension.chartData.map((data, index) => (
              <div key={index} className="chart-bar-container">
                <div 
                  className="chart-bar" 
                  style={{ 
                    height: `${data.value}%`,
                    backgroundColor: getScoreColor(data.value)
                  }}
                ></div>
                <div className="chart-label">{data.month}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="sustainability-index">
      <div className="section-header">
        <h2>{region} Sustainability Index (RSI)</h2>
        <div className="header-actions">
          <select 
            className="region-selector"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
          >
            <option value="DMV Region">DMV Region</option>
            <option value="Maryland">Maryland</option>
            <option value="Virginia">Virginia</option>
            <option value="District of Columbia">District of Columbia</option>
          </select>
          <button 
            className="report-button"
            onClick={handleGenerateReport}
          >
            <FaDownload /> Generate Report
          </button>
        </div>
      </div>
      
      <div className="rsi-overview">
        <div className="rsi-score-card">
          <div className="rsi-score" style={{ color: getScoreColor(overallScore) }}>
            {overallScore}
          </div>
          <div className="rsi-score-label">Overall RSI Score</div>
          <div className="rsi-meta">
            <div className="rsi-status">Status: {complianceStatus}</div>
            <div className="rsi-updated">Last updated: {lastUpdated}</div>
          </div>
        </div>
        
        <div className="rsi-info">
          <h3>About the {region} Sustainability Index</h3>
          <p>
            The RSI calculates ecosystem health across 12 dimensions, providing a comprehensive 
            view of agricultural sustainability in the {region}. Local governments can link subsidies 
            to RSI scores and automatically generate compliance reports for SDG tracking.
          </p>
          <p>
            Data is sourced from USDA CroplandCROS and local environmental monitoring stations.
          </p>
          <div className="rsi-legend">
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: 'var(--accent-green)' }}></span>
              <span className="legend-label">Good (80-100)</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: 'var(--accent-yellow)' }}></span>
              <span className="legend-label">Moderate (60-79)</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: 'var(--accent-red)' }}></span>
              <span className="legend-label">Needs Improvement (0-59)</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="rsi-dimensions">
        <h3>Sustainability Dimensions</h3>
        <p className="dimensions-instruction">Click on any dimension card to view detailed information and metrics.</p>
        <div className="dimensions-grid">
          {dimensions.map(dimension => (
            <div 
              key={dimension.id} 
              className={`dimension-card ${expandedDimension === dimension.id ? 'expanded' : ''}`}
              data-score={getScoreCategory(dimension.score)}
              data-id={dimension.id}
              onClick={() => handleExpandDimension(dimension.id)}
            >
              <div className="dimension-header">
                <div className="dimension-icon">{dimension.icon}</div>
                <div className="dimension-details">
                  <h4>{dimension.name}</h4>
                  <div 
                    className="dimension-score" 
                    style={{ color: getScoreColor(dimension.score) }}
                  >
                    {dimension.score}
                  </div>
                  <div 
                    className="dimension-trend"
                    style={{ color: getTrendColor(dimension.trend) }}
                  >
                    {dimension.trend} {getTrendIcon(dimension.trend)}
                  </div>
                </div>
                <button 
                  className="expand-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExpandDimension(dimension.id);
                  }}
                >
                  {expandedDimension === dimension.id ? <FaChevronUp /> : <FaChevronDown />}
                </button>
              </div>
              
              {expandedDimension === dimension.id && renderExpandedContent(dimension)}
            </div>
          ))}
        </div>
      </div>
      
      <div className="rsi-actions">
        <button className="action-button">
          <FaHistory style={{ marginRight: '8px' }} />
          View Historical Data
        </button>
        <button className="action-button">
          <FaGlobeAmericas style={{ marginRight: '8px' }} />
          Compare with Other Regions
        </button>
        <button className="action-button">
          <FaBullseye style={{ marginRight: '8px' }} />
          Set Improvement Goals
        </button>
        <button className="action-button">
          <FaDatabase style={{ marginRight: '8px' }} />
          View USDA CroplandCROS Data
        </button>
      </div>
    </div>
  );
};

export default SustainabilityIndex; 